import {
  PageContainer as AntdPageContainer,
  type PageContainerProps as AntdPageContainerProps,
} from '@ant-design/pro-components'
import { normalizeUrl } from '@pkg/shared/utils/api.utils'
import { useRouterState } from '@tanstack/react-router'
import { Alert, Divider, Flex, Space } from 'antd'
import type { ReactNode } from 'react'
import { Children, useMemo } from 'react'
import { useStaticDataMerge } from '../hooks'
import { useAppBreadcrumbs } from '../hooks/useAppBreadcrumbs'
import {
  type PageContainerBreadcrumbMode,
  PageContainerRuntimeProvider,
  usePageContainerRuntimeProps,
} from './PageContainerRuntime'

type BaseAntdPageContainerProps = Omit<AntdPageContainerProps, 'children' | 'breadcrumb'>

interface LayoutRouteNode {
  path?: string
  name?: ReactNode
  routes?: LayoutRouteNode[]
  children?: LayoutRouteNode[]
}

function collectNamedAbsolutePaths(
  nodes: LayoutRouteNode[] | undefined,
  acc: Array<{ path: string; name: ReactNode }> = [],
) {
  if (!nodes) return acc
  for (const node of nodes) {
    if (node.path?.startsWith('/') && node.name != null) {
      const normalizedPath = normalizeUrl(node.path)
      if (normalizedPath) {
        acc.push({ path: normalizedPath, name: node.name })
      }
    }
    collectNamedAbsolutePaths(node.routes ?? node.children, acc)
  }
  return acc
}

function matchesPath(itemPath: string, pathname: string): boolean {
  const itemSegments = itemPath.split('/').filter(Boolean)
  const pathSegments = pathname.split('/').filter(Boolean)

  if (itemSegments.length > pathSegments.length) {
    return false
  }

  for (let i = 0; i < itemSegments.length; i++) {
    const itemSegment = itemSegments[i]
    const pathSegment = pathSegments[i]

    if (!itemSegment || !pathSegment) {
      return false
    }

    if (itemSegment?.startsWith('$')) {
      continue
    }

    if (itemSegment !== pathSegment) {
      return false
    }
  }

  return true
}

export interface PageContainerProps extends BaseAntdPageContainerProps {
  children: React.ReactNode
  /** Make the last crumb a link instead of plain text */
  renderLastAsLink?: boolean
  /** When set, collapse middle crumbs by inserting an ellipsis */
  maxItems?: number
  /** auto = derived from router, off = no breadcrumb, custom = use provided breadcrumb prop */
  breadcrumbMode?: PageContainerBreadcrumbMode
  breadcrumb?: AntdPageContainerProps['breadcrumb']
  /** Derive page title from the currently matched route name when no explicit title is set */
  autoTitle?: boolean
  /** Optional route definitions used for automatic title resolution */
  titleRoutes?: LayoutRouteNode[]
}

function PageContainerContent({
  children,
  renderLastAsLink = true,
  maxItems,
  breadcrumbMode = 'auto',
  breadcrumb,
  autoTitle = true,
  titleRoutes,
  ...rest
}: PageContainerProps) {
  const runtimeProps = usePageContainerRuntimeProps()
  const { data: layout } = useStaticDataMerge(data => data.layout, {})
  const pathname = useRouterState({ select: s => normalizeUrl(s.location.pathname) ?? '' })

  const {
    alerts: runtimeAlerts,
    renderLastAsLink: runtimeRenderLastAsLink,
    maxItems: runtimeMaxItems,
    breadcrumbMode: runtimeBreadcrumbMode,
    breadcrumb: runtimeBreadcrumb,
    ...runtimeAntdProps
  } = runtimeProps
  const mergedBreadcrumbMode = runtimeBreadcrumbMode ?? breadcrumbMode
  const autoBreadcrumbItems = useAppBreadcrumbs({
    renderLastAsLink: runtimeRenderLastAsLink ?? renderLastAsLink,
    maxItems: runtimeMaxItems ?? maxItems,
  })

  const derivedTitle = useMemo(() => {
    if (!autoTitle) {
      return undefined
    }

    const candidates = [
      ...collectNamedAbsolutePaths(titleRoutes),
      ...collectNamedAbsolutePaths((layout as { route?: { routes?: LayoutRouteNode[] } })?.route?.routes),
    ]
    let best: { path: string; name: ReactNode } | undefined

    for (const candidate of candidates) {
      if (matchesPath(candidate.path, pathname) && (!best || candidate.path.length > best.path.length)) {
        best = candidate
      }
    }

    return best?.name
  }, [autoTitle, layout, pathname, titleRoutes])

  const title = runtimeAntdProps.header?.title ?? rest.header?.title ?? derivedTitle
  const semanticTitle =
    typeof title === 'string' || typeof title === 'number' ? (
      // biome-ignore lint/a11y/useSemanticElements: ProLayout renders its title inside an inline span.
      <span aria-level={1} role="heading">
        {title}
      </span>
    ) : (
      title
    )
  const mergedHeader = {
    ...rest.header,
    ...runtimeAntdProps.header,
    title: semanticTitle,
    footer: runtimeAntdProps.header?.footer ?? rest.header?.footer ?? (
      <Divider size="small" style={{ marginBottom: 0 }} />
    ),
  }

  const mergedHeaderExtras = Children.toArray(mergedHeader.extra)
  if (mergedHeaderExtras.length > 0) {
    mergedHeader.extra = (
      <Space separator={<Divider vertical />} size="small">
        {mergedHeaderExtras}
      </Space>
    )
  }

  const mergedProps: BaseAntdPageContainerProps = {
    ...rest,
    ...runtimeAntdProps,
    header: mergedHeader,
  }

  const resolvedBreadcrumb =
    mergedBreadcrumbMode === 'off'
      ? undefined
      : mergedBreadcrumbMode === 'custom'
        ? (runtimeBreadcrumb ?? breadcrumb)
        : { items: autoBreadcrumbItems }

  const alerts = runtimeAlerts ?? []
  const hasAlerts = alerts.length > 0
  const originalBreadcrumbRender = mergedProps.breadcrumbRender
  const shouldRenderAlertBreadcrumb = mergedBreadcrumbMode !== 'off' && hasAlerts

  const breadcrumbRender = shouldRenderAlertBreadcrumb
    ? (
        pageHeaderProps: Parameters<Exclude<typeof originalBreadcrumbRender, false | undefined>>[0],
        defaultBreadcrumb: ReactNode,
      ) => {
        const breadcrumbDom =
          typeof originalBreadcrumbRender === 'function'
            ? originalBreadcrumbRender(pageHeaderProps, defaultBreadcrumb)
            : defaultBreadcrumb

        return (
          <Flex vertical gap="small">
            {breadcrumbDom}
            {alerts.map(alert => (
              <Alert key={String(alert.title ?? alert.type ?? 'alert')} {...alert} />
            ))}
          </Flex>
        )
      }
    : originalBreadcrumbRender

  return (
    <AntdPageContainer {...mergedProps} breadcrumb={resolvedBreadcrumb} breadcrumbRender={breadcrumbRender}>
      {children}
    </AntdPageContainer>
  )
}

export function PageContainer(props: PageContainerProps) {
  return (
    <PageContainerRuntimeProvider>
      <PageContainerContent {...props} />
    </PageContainerRuntimeProvider>
  )
}

export type { RuntimePageContainerProps } from './PageContainerRuntime'
export {
  useAlert,
  useAlerts,
  useExtra,
  useExtras,
  usePageContainerController,
  usePageContainerOverride,
  useTabs,
} from './PageContainerRuntime'
